import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiError } from "@/lib/api";
import { ASSETS, BRAND } from "@/lib/brand";

export default function AdminLoginPage() {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const { register, handleSubmit, formState: { errors } } = useForm({
    defaultValues: { email: "", password: "" },
  });

  if (typeof window !== "undefined" && localStorage.getItem("pe_admin_token")) {
    return <Navigate to="/admin" replace />;
  }

  const onSubmit = async ({ email, password }) => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("pe_admin_token", data.access_token);
      localStorage.setItem("pe_admin_user", JSON.stringify(data.user));
      toast.success("Signed in.", { description: `Welcome back, ${data.user?.name || "Admin"}.` });
      navigate("/admin", { replace: true });
    } catch (e) {
      toast.error("Sign in failed.", { description: formatApiError(e, "Invalid email or password.") });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="admin-login-page" className="min-h-screen bg-pe-ink flex flex-col items-center justify-center px-5 py-16">
      <div className="w-full max-w-md">
        <div className="text-center mb-10">
          <img src={ASSETS.logo_light} alt="Pizza Excellente" className="h-16 md:h-20 w-auto object-contain mx-auto" />
          <h1 className="font-serif text-4xl text-pe-cream mt-6">Admin Sign In</h1>
          <p className="text-pe-cream/60 mt-3 text-sm">Enquiries dashboard access</p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="bg-pe-cream p-8 space-y-5" noValidate>
          <div className="space-y-1.5">
            <Label htmlFor="admin-email" className="text-xs tracking-[0.15em] uppercase text-pe-ink/70 font-medium">Email</Label>
            <Input
              id="admin-email"
              data-testid="admin-login-email"
              type="email"
              autoComplete="email"
              placeholder="admin@pizzaexcellente.com"
              className="rounded-none border-pe-ink/20 bg-white focus-visible:ring-pe-maroon focus-visible:border-pe-maroon h-12"
              {...register("email", { required: "Email is required" })}
            />
            {errors.email && <p className="text-xs text-pe-maroon">{errors.email.message}</p>}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="admin-password" className="text-xs tracking-[0.15em] uppercase text-pe-ink/70 font-medium">Password</Label>
            <Input
              id="admin-password"
              data-testid="admin-login-password"
              type="password"
              autoComplete="current-password"
              placeholder="••••••••"
              className="rounded-none border-pe-ink/20 bg-white focus-visible:ring-pe-maroon focus-visible:border-pe-maroon h-12"
              {...register("password", { required: "Password is required" })}
            />
            {errors.password && <p className="text-xs text-pe-maroon">{errors.password.message}</p>}
          </div>
          <button
            data-testid="admin-login-submit"
            disabled={submitting}
            type="submit"
            className="w-full inline-flex items-center justify-center gap-2 bg-pe-maroon text-white px-8 py-4 text-[12px] tracking-[0.15em] font-medium uppercase hover:bg-pe-maroon-deep disabled:opacity-60 transition-colors"
          >
            {submitting ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />}
            {submitting ? "Signing in..." : "Sign in"}
          </button>
        </form>

        <p className="text-center text-xs text-pe-cream/40 mt-8">
          Protected area · {BRAND.name}
        </p>
      </div>
    </div>
  );
}
